import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import useCustomNavigate from "../../../common_nextgen/common_components/Navigation/NextGenNavigation";
import { PRICE_SCHEDULE_MODES } from "../../../common_nextgen/utils/Constants";
import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../../common_nextgen/utils/UtilFunctions";
import { axios_helper_no_header } from "../../../common_nextgen/axios/AxiosHelper";
import { useToast } from "../../../common_nextgen/hooks/ToastContext";

const usePriceScheduleForm = (mode) => {
  const location = useLocation();
  const {
    selectedPriceSchedules = [],
    previousScreen = "/price-schedule",
    currentIndex: initialIndex = 0,
  } = location.state || {};
  const navigateToPage = useCustomNavigate();
  const [selectedOption, setSelectedOption] = useState("no");
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentMode, setCurrentMode] = useState(mode);
  const [initialFormData, setInitialFormData] = useState({});
  const [dataForSubmission, setDataForSubmission] = useState({});
  const [constraintErrors, setConstraintErrors] = useState([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const toast = useToast();

  const screenInfo = {
    ADD: ["Add Price Schedule", "RCPRC008"],
    CHANGE: ["Price Schedule Detail", "RCPRC004"],
  };

  const handleBack = () => {
    if (currentIndex < selectedPriceSchedules.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentMode(mode);
    } else {
      navigateToPage(previousScreen, {
        priceSchedule: selectedPriceSchedules[currentIndex],
      });
    }
  };

  const openConfirmationModal = () => setIsConfirmationModalOpen(true);
  const closeConfirmationModal = () => setIsConfirmationModalOpen(false);

  const getPageTitle = () => {
    const [screenTitle, screenNumber] = isAddMode(mode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  };

  const submitFormData = (data) => {
    setDataForSubmission(data);
    openConfirmationModal();
  };

  const submitForm = async () => {
    let url = "";
    let verb = "";
    let screenNumber = "";

    if (selectedOption === "no") {
      closeConfirmationModal();
      return;
    }

    if (isAddMode(currentMode)) {
      url = "api/priceschedule/create";
      verb = "POST";
      screenNumber = screenInfo.ADD[1];
    } else {
      url = "api/priceschedule/update";
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];

      if (
        JSON.stringify(dataForSubmission) === JSON.stringify(initialFormData)
      ) {
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "No Change of Information",
          life: 3000,
        });
        closeConfirmationModal();
        return;
      }
    }

    const data = { ...dataForSubmission, screenNumber };

    try {
      const { responseStatus } = await axios_helper_no_header(
        verb,
        url,
        data,
        null
      );
      toast.current.show({
        severity: "success",
        summary: "",
        detail: responseStatus.message,
        life: 3000,
      });
    } catch (error) {
      const { responseStatus: { displayErrorFields = null, message } = {} } =
        error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    } finally {
      closeConfirmationModal();
    }
  };

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};

    if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "\u2190 Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F7 = () =>
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "Page under construction",
          life: 3000,
        });
      keyFunc.F9 = () =>
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "Page under construction",
          life: 3000,
        });
      keyFunc.F10 = () =>
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "Page under construction",
          life: 3000,
        });
      keyFunc.F12 = handleBack;
      keyFunc.F14 = () =>
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "Page under construction",
          life: 3000,
        });
      keyFunc.F15 = () =>
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "Page under construction",
          life: 3000,
        });
      keyFunc.View = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
      keyName.F12 = "\u2190 Back";
      keyName.View = "View";
      keyName.F7 = "Client Price Schedules";
      keyName.F9 = "Client Limits";
      keyName.F10 = "Copy";
      keyName.F14 = "Note";
      keyName.F15 = "Criteria";
    } else if (isReadOnlyMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyFunc.F7 = () =>
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "Page under construction",
          life: 3000,
        });
      keyFunc.F9 = () =>
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "Page under construction",
          life: 3000,
        });
      keyFunc.F10 = () =>
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "Page under construction",
          life: 3000,
        });

      keyFunc.F14 = () =>
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "Page under construction",
          life: 3000,
        });
      keyFunc.F15 = () =>
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "Page under construction",
          life: 3000,
        });
      keyFunc.Edit = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      keyName.F12 = "\u2190 Back";
      keyName.Edit = "Edit";
      keyName.F7 = "Client Price Schedules";
      keyName.F9 = "Client Limits";
      keyName.F10 = "Copy";
      keyName.F14 = "Note";
      keyName.F15 = "Criteria";
    }

    return [keyFunc, keyName];
  }, [currentMode, currentIndex]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const url = isAddMode(currentMode)
        ? "api/priceschedule/addform/lookup"
        : `api/priceschedule/${selectedPriceSchedules[currentIndex]}`;
      try {
        const { responseData } = await axios_helper_no_header("GET", url);
        setInitialFormData(responseData);
      } catch (error) {
        const { responseStatus: { message = "" } = {} } = error.data;
        message &&
          toast.current.show({
            severity: "error",
            summary: "",
            detail: message,
            life: 3000,
          });
      }
    };

    fetchInitialData();
  }, [mode, currentIndex]);

  return {
    currentMode,
    initialFormData,
    constraintErrors,
    isConfirmationModalOpen,
    keyFunc,
    keyName,
    getPageTitle,
    submitFormData,
    closeConfirmationModal,
    submitForm,
    setSelectedOption,
  };
};

export default usePriceScheduleForm;
